import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "./axiosConfig";
import { clearMerchantAuth } from "./auth";
import { useLanguage } from "./LanguageContext"; // Import du contexte
import { Lock, ArrowLeft, Save, Languages } from "lucide-react";

export default function MerchantSettings() {
  const navigate = useNavigate();
  const { t, language, setLanguage } = useLanguage();

  const [form, setForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    setError(null);
    setSuccess(false);
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!form.currentPassword || !form.newPassword) {
      return setError(t("error_required"));
    }

    if (form.newPassword.length < 6) {
      return setError(t("error_short"));
    }

    if (form.newPassword !== form.confirmPassword) {
      return setError(t("error_mismatch"));
    }

    try {
      setLoading(true);

      await api.put("/merchants/me/password", {
        currentPassword: form.currentPassword,
        newPassword: form.newPassword,
      });

      setSuccess(true);
      setForm({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });

      setTimeout(() => {
        clearMerchantAuth();
        navigate("/merchant-login");
      }, 2000);

    } catch (err) {
      setError(
        err.response?.data?.error || t("error_server")
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F7FA] p-8 font-sans">
      <div className="max-w-xl mx-auto space-y-6">

        {/* HEADER */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 bg-white border rounded-xl hover:bg-gray-50 transition-colors"
          >
            <ArrowLeft size={18} />
          </button>
          <h1 className="text-2xl font-extrabold">{t("navSettings")}</h1>
        </div>

        {/* SECTION LANGUE */}
        <div className="p-8 bg-white border shadow rounded-3xl">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 text-blue-600 rounded-xl bg-blue-50">
              <Languages size={20} />
            </div>
            <div>
              <h2 className="text-lg font-bold">{t("language_choice")}</h2>
            </div>
          </div>
          
          <div className="flex gap-4">
            <button
              onClick={() => setLanguage("fr")}
              className={`flex-1 py-3 px-4 rounded-xl border-2 font-bold transition-all ${
                language === "fr" 
                ? "border-[#FF3B30] bg-red-50 text-[#FF3B30]" 
                : "border-gray-100 bg-gray-50 text-gray-500 hover:border-gray-200"
              }`}
            >
              Français
            </button>
            <button
              onClick={() => setLanguage("ar")}
              className={`flex-1 py-3 px-4 rounded-xl border-2 font-bold transition-all ${
                language === "ar" 
                ? "border-[#FF3B30] bg-red-50 text-[#FF3B30]" 
                : "border-gray-100 bg-gray-50 text-gray-500 hover:border-gray-200"
              }`}
            >
              العربية
            </button>
          </div>
        </div>

        {/* SECTION SÉCURITÉ */}
        <div className="p-8 bg-white border shadow rounded-3xl">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 text-red-600 rounded-xl bg-red-50">
              <Lock size={20} />
            </div>
            <div>
              <h2 className="text-lg font-bold">{t("security")}</h2>
              <p className="text-sm text-gray-400">
                {t("change_password_desc")}
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <Input 
              label={t("current_password")} 
              name="currentPassword" 
              type="password" 
              value={form.currentPassword} 
              onChange={handleChange} 
            />
            <Input 
              label={t("new_password")} 
              name="newPassword" 
              type="password" 
              value={form.newPassword} 
              onChange={handleChange} 
            />
            <Input 
              label={t("confirm_new_password")} 
              name="confirmPassword" 
              type="password" 
              value={form.confirmPassword} 
              onChange={handleChange} 
            />

            {error && <div className="p-3 text-sm text-red-600 bg-red-50 rounded-lg">{error}</div>}
            {success && <div className="p-3 text-sm text-green-600 bg-green-50 rounded-lg">{t("pwd_changed")}</div>}

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 py-4 bg-[#FF3B30] text-white rounded-xl font-bold shadow-lg hover:bg-red-600 transition-all disabled:opacity-50"
            >
              <Save size={18} />
              {loading ? "..." : t("update_btn")}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function Input({ label, ...props }) {
  return (
    <div>
      <label className="block mb-1 text-sm font-semibold text-gray-700">{label}</label>
      <input 
        {...props} 
        className="w-full px-4 py-3 border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:ring-2 focus:ring-red-100 outline-none transition-all" 
      />
    </div>
  );
}
